import { fetchBindingOrderLineTracking } from '@/services';
import { useCustomStore } from '@/store';
import { ButtonGroupStatus, FrequencyUnit, OrderState, DeliveryType, PickupStatus } from '@/types';

export default {
  /**
   * 根据订单状态获取按钮配置信息
   * @param data - 订单数据
   * @param callbacks - 订单相关操作的回调函数
   * @param otherOptions - 订单相关操作的回调函数
   */
  getConfig: (data: AnyObject, callbacks: AnyObject, ...rest: any) => {
    const state = data.state;

    // 是否订单列表场景
    const isListScene = !!rest?.[0];

    // 订单是否可申请售后
    const isOrderAvailableAfterSale = data.customFields.isAvailableAfterSale;

    // 按钮尺寸
    const buttonSize = rest?.[1];

    //  是否包含支付有礼活动
    const isPaymentReward = isListScene && data.isPaymentRewardActivity;

    // 订单商品是否全部在申请售后
    const proAllAfterSale =
      data.lines.filter((item: AnyObject) => !item?.customFields.afterSaleLine).length <= 1;

    // 是否需要物流
    const hasLogistics = data.fulfillments?.[0]?.method !== 'noLogistics';

    const lookupLogistics = hasLogistics
      ? {
          text: '查看物流',
          status: ButtonGroupStatus.normalA,
          size: buttonSize,
          onClick: callbacks['lookupLogistics'],
        }
      : null;

    const confirmReceipt = {
      text: '确认收货',
      status: ButtonGroupStatus.active,
      size: buttonSize,
      onClick: callbacks['confirmReceipt'],
    };

    const deleteOrder = {
      text: '删除订单',
      status: ButtonGroupStatus.normalA,
      size: buttonSize,
      onClick: callbacks['deleteOrder'],
    };

    const cancelOrder = {
      text: '取消订单',
      status: ButtonGroupStatus.normalA,
      size: buttonSize,
      onClick: callbacks['cancelOrder'],
    };

    const continuePay = {
      text: '继续支付',
      status: ButtonGroupStatus.active,
      size: buttonSize,
      onClick: callbacks['continuePay'],
    };

    // data.orderPickup?.deliveryType只有在自提方式下返回，因此在非自提方式下，data.orderPickup?.deliveryType === DeliveryType.pickup为false，modifyShipAddress为null
    const modifyShipAddress = !(data.orderPickup?.deliveryType === DeliveryType.pickup) ? {
      text: '修改地址',
      status: ButtonGroupStatus.normalA,
      size: buttonSize,
      onClick: callbacks['modifyShipAddress'],
    } : null;

    const bulkRefund =
      isOrderAvailableAfterSale && !isListScene
        ? {
            text: '批量退款',
            status: ButtonGroupStatus.normalA,
            size: buttonSize,
            onClick: callbacks['bulkRefund'],
          }
        : null;

    const payReward = isPaymentReward
      ? {
          text: '点我领券', // 支付有礼
          status: ButtonGroupStatus.active,
          size: buttonSize,
          onClick: callbacks['payReward'],
        }
      : null;
    
    const pickupCode =
      data.orderPickup?.deliveryType === DeliveryType.pickup &&
      data.orderPickup?.status === PickupStatus.pending && 
      data.orderPickup?.pickupCode
        ? {
            text: '提货码',
            status: ButtonGroupStatus.active,
            size: buttonSize,
            onClick: callbacks['pickupCode'],
          }
        : null;

    // 下单支付时间
    const statusCom = (data: AnyObject) => {
      const limit = 1000 * 60 * 30;
      const diff = Date.now() - new Date(data.orderPlacedAt).getTime();

      // 不显示批量退款按钮
      const result =
        data.lines.filter((item: AnyObject) => !item?.customFields.afterSaleLine).length <= 1;
      // 是否超时
      if (diff > limit && !result) return [payReward, bulkRefund];

      if (diff > limit && result) return [payReward];

      if (diff <= limit && !result) return [payReward, bulkRefund, modifyShipAddress];

      if (diff <= limit && result) return [payReward, modifyShipAddress];
      // 是否都在售后状态
      const isAllLineAfterSale = data?.lines.every(
        (it: AnyObject) => !!it.customFields.afterSaleLine
      );
      if (isAllLineAfterSale) return [payReward];

      return [payReward];
    };

    const config: AnyObject = {
      [OrderState.ArrangingPayment]: [cancelOrder, modifyShipAddress, continuePay],
      // 2023-06-16: 下单支付时间小于指定时间才能修改地址
      [OrderState.PaymentSettled]: [pickupCode, ...statusCom(data)],
      [OrderState.Shipped]: !proAllAfterSale
        ? [payReward, bulkRefund, lookupLogistics, confirmReceipt]
        : [payReward, lookupLogistics, confirmReceipt],
      [OrderState.Delivered]: !proAllAfterSale
        ? [payReward, bulkRefund, lookupLogistics, confirmReceipt]
        : [payReward, lookupLogistics, confirmReceipt],
      [OrderState.ConfirmReceiptOfGoods]: !proAllAfterSale
        ? [payReward, bulkRefund, deleteOrder, lookupLogistics]
        : [payReward, deleteOrder, lookupLogistics],
      [OrderState.ReviewSettled]: !proAllAfterSale
        ? [payReward, bulkRefund, deleteOrder, lookupLogistics]
        : [payReward, deleteOrder, lookupLogistics],
      [OrderState.Cancelled]: [deleteOrder],
    };

    return config[state]?.filter((it: AnyObject) => !!it);
  },
  /**
   * 根据订单状态获取文本信息
   * @param state - 订单状态
   */
  getStateText: (state: OrderState) => {
    const config: AnyObject = {
      [OrderState.ArrangingPayment]: '等待付款',
      [OrderState.PaymentSettled]: '等待发货',
      [OrderState.Shipped]: '等待收货',
      [OrderState.Delivered]: '等待收货',
      [OrderState.ConfirmReceiptOfGoods]: '交易完成',
      [OrderState.ReviewSettled]: '交易完成',
      [OrderState.Cancelled]: '交易关闭',
    };
    return config[state];
  },
  /**
   * 订单引导页面来源
   * @param type
   * @param orderId
   * @param productVariantId
   * @returns
   */
  bindOrderLineTrack: async (type: String, orderId: string, productVariantId: string) => {
    const customStore = useCustomStore();

    // 自定义页面类型
    const customPageType = (routeObject: AnyObject) => {
      if (routeObject.route === 'pages/home/index') {
        return {
          trackingPageType: 'activePage',
          trackingPageId: customStore.homeCustomId,
          report: true,
        };
      } else if (routeObject.route === 'pages/classify/index') {
        return {
          trackingPageType: 'activePage',
          trackingPageId: customStore.classifyCustomId,
          report: true,
        };
      } else if (routeObject.route === 'pages/subpack-main/activity/index') {
        return {
          trackingPageType: 'activePage',
          trackingPageId: customStore.activityCustomId,
          report: true,
        };
      } else if (routeObject.route === 'pages/subpack-main/member/detail/index') {
        return {
          trackingPageType: 'activePage',
          trackingPageId: customStore.activityCustomId,
          report: true,
        };
      } else {
        return { trackingPageType: '', trackingPageId: '', report: false };
      }
    };

    const pages = getCurrentPages();

    if (type === 'addToCart') {
      // 判断路由栈长度小于等于2 且 没有来源id
      if (
        pages[pages.length - 1].route === 'pages/subpack-main/commodity/index' &&
        pages.length > 1
      ) {
        const { trackingPageType, trackingPageId, report } = customPageType(
          pages[pages.length - 2]
        );
        if (!report) return;
        await fetchBindingOrderLineTracking(
          orderId,
          productVariantId,
          trackingPageType,
          trackingPageId
        );
      } else {
        const { trackingPageType, trackingPageId, report } = customPageType(
          pages[pages.length - 1]
        );
        if (!report) return;
        await fetchBindingOrderLineTracking(
          orderId,
          productVariantId,
          trackingPageType,
          trackingPageId
        );
      }
    } else {
      if (
        pages[pages.length - 2].route === 'pages/subpack-main/commodity/index' &&
        pages.length > 2
      ) {
        const { trackingPageType, trackingPageId, report } = customPageType(
          pages[pages.length - 3]
        );
        if (!report) return;
        await fetchBindingOrderLineTracking(
          orderId,
          productVariantId,
          trackingPageType,
          trackingPageId
        );
      } else {
        const { trackingPageType, trackingPageId, report } = customPageType(
          pages[pages.length - 2]
        );
        if (!report) return;
        await fetchBindingOrderLineTracking(
          orderId,
          productVariantId,
          trackingPageType,
          trackingPageId
        );
      }
    }
  },
  /**
   * 获取单位文本
   * @param unit
   * @returns
   */
  getUnit: (unit: FrequencyUnit) => {
    return unit === FrequencyUnit.Week ? '周' : unit === FrequencyUnit.Month ? '月' : '天';
  },
  /**
   * 是否展示退、换货入口
   * @param orderState - 订单状态
   * @returns
   */
  isDisplayReturnOrExchangeEntry: (orderState: OrderState) => {
    return ![OrderState.ArrangingPayment, OrderState.PaymentSettled].includes(orderState);
  },
};
